import {
  getMatches,
} from '@homeservices/api-kit/search/promatch';

import {
  getPublicProfiles,
} from '@homeservices/api-kit/search/publicprofile';

import find from 'lodash/find';
import isZipcodeChosen from '@/utils/is-zipcode-chosen';

const MAX_SELECTED_PROFILE_NUM = 5;

function initialState () {
  return {
    publicProfiles: [],
    preSelectedPublicProfilesIds: [],
    selectedPublicProfilesIds: [],
    touchedPublicProfilesIds: [],
    shouldShowMoreProfiles: false,
  };
}

export default {
  namespaced: true,
  state: initialState,
  getters: {
    publicProfiles (state) {
      return state.publicProfiles;
    },
    matchedPublicProfilesIds (state) {
      // this list is created only once, it won't change thereafter
      return state.publicProfiles.map(provider => provider.id);
    },
    nonPreSelectedPublicProfilesIds (state) {
      return state.publicProfiles
        .filter(provider => !state.preSelectedPublicProfilesIds.includes(provider.id)).slice(0, 3)
        .map(provider => provider.id);
    },
    extraSelectedPublicProfileIds (state) {
      return state.publicProfiles
        .filter(provider => !state.preSelectedPublicProfilesIds.includes(provider.id)).slice(3)
        .map(provider => provider.id);
    },
    preSelectedPublicProfilesIds (state) {
      return state.preSelectedPublicProfilesIds;
    },
    maxSelectedProfiles () {
      return MAX_SELECTED_PROFILE_NUM;
    },
    selectedPublicProfilesIds (state) {
      return state.selectedPublicProfilesIds;
    },
    publicProfileCredentials (state) {
      // TODO (alok), add license logic when core starts sending value
      return (id) => {
        const publicProfile = find(state.publicProfiles, { id });
        const hasLicense = publicProfile.documents && find(publicProfile.documents, { document_type: 'license' });
        const hasInsuredAttr = find(publicProfile.service_provider.attribute_values, { service_provider_attribute: 'insured_amt' });

        return {
          isLicensed: hasLicense,
          licenseNumber: hasLicense && hasLicense.document_identifier,
          licenseState: hasLicense && hasLicense.state,
          isInsured: hasInsuredAttr && hasInsuredAttr.value > 0,
          insuredAmount: hasInsuredAttr && hasInsuredAttr.value,
        };
      };
    },
    topPro (state) {
      return (id) => {
        const publicProfile = find(state.publicProfiles, { id });
        const topProAttr = find(publicProfile.service_provider.attribute_values, { service_provider_attribute: 'pro_badge_eligible' }) || null;
        return topProAttr;
      };
    },
    isValid (state) {
      return !!state.selectedPublicProfilesIds.length;
    },
    isSelected (state) {
      return id => state.selectedPublicProfilesIds.includes(id);
    },
    isPreSelected (state) {
      return id => state.preSelectedPublicProfilesIds.includes(id);
    },
    isTouched (state) {
      return id => state.touchedPublicProfilesIds.includes(id);
    },
    isDisabled (state, getters) {
      return id => state.selectedPublicProfilesIds.length === MAX_SELECTED_PROFILE_NUM
        && !getters.isSelected(id);
    },
    shouldShowMoreProfiles (state) {
      return state.shouldShowMoreProfiles;
    },
  },
  actions: {
    async fetchPublicProfiles ({ state, commit }, { serviceId, zipcode, seed }) {
      if (!isZipcodeChosen(zipcode)) {
        return;
      }

      const { data: {
        results,
        pro_match_request_id,
        preselected_service_provider_occupation_ids: preSelectedPublicProfilesIds,
      } } = await getMatches({
        service_id: serviceId,
        zipcode,
        random_seed: seed,
        show_more_pros: true,
      });

      commit('setPreselectedProfiles', preSelectedPublicProfilesIds || []);
      commit('setProMatchRequestId', pro_match_request_id);

      const publicProfilesIds = results.map(provider => provider.service_provider_occupation_id);
      const { data } = await getPublicProfiles({ ids: publicProfilesIds.join(',') });

      // state.publicProfiles should always maintain the order of ids what was received
      // from proMatch call,
      const publicProfiles = [];
      publicProfilesIds.forEach((id) => {
        publicProfiles.push(find(data.results, { id }));
      });

      commit('setPublicProfiles', publicProfiles);
      if (!state.selectedPublicProfilesIds.length) {
        commit('setSelectedPublicProfilesIds', preSelectedPublicProfilesIds);
      }
    },
    toggleSelectedPublicProfile ({ commit }, publicProfileId) {
      commit('toggleSelectedPublicProfile', publicProfileId);
    },
    clear ({ commit }) {
      commit('clear');
    },
  },
  mutations: {
    setPublicProfiles (state, publicProfiles) {
      state.publicProfiles = publicProfiles;
    },
    setPreselectedProfiles (state, preSelectedPublicProfilesIds) {
      state.preSelectedPublicProfilesIds = preSelectedPublicProfilesIds;
    },
    setSelectedPublicProfilesIds (state, publicProfilesIds) {
      state.selectedPublicProfilesIds = [...publicProfilesIds];
    },
    setProMatchRequestId (state, proMatchRequestId) {
      state.proMatchRequestId = proMatchRequestId;
    },
    toggleSelectedPublicProfile (state, publicProfileId) {
      // toggle PublicProfilesId in selectedPublicProfilesIds
      const proIndex = state.selectedPublicProfilesIds.indexOf(publicProfileId);
      const isProTouched = state.touchedPublicProfilesIds.includes(publicProfileId);

      if (proIndex >= 0) {
        state.selectedPublicProfilesIds.splice(proIndex, 1);
      } else {
        state.selectedPublicProfilesIds.push(publicProfileId);
      }

      // if the PublicProfilesId is not in touchedPublicProfilesIds, we need to add it.
      // this helps us keep track of profile that have been clicked.
      if (!isProTouched) {
        state.touchedPublicProfilesIds.push(publicProfileId);
      }
    },
    clear (state) {
      Object.assign(state, initialState());
    },
    showMoreProfiles (state) {
      state.shouldShowMoreProfiles = true;
    },
  },
};
